import { decodeBase64Utf8 } from './base64'
import { ContentError } from './errors'
import { fetchWithTimeout, retryAfterOf } from './net'
import type { ContentSource, SourceLoadOptions, SourceResult } from './types'
import { parseBundle } from './validate'

export interface GiteeSourceOptions {
  readonly owner: string
  readonly repo: string
  readonly ref?: string
  readonly bundlePath?: string
  readonly apiBase?: string
}

interface GiteeTreeEntry {
  readonly path: string
  readonly type: string
  readonly sha: string
}

interface GiteeTree {
  readonly tree?: readonly GiteeTreeEntry[]
  readonly truncated?: boolean
}

interface GiteeBlob {
  readonly content?: string
  readonly encoding?: string
  readonly sha?: string
}

const DEFAULT_API = 'https://gitee.com/api/v5'

/**
 * 公开仓库的只读内容源。
 * tree 一次拿到全部路径，找出 dist/content.json 的 blob sha；sha 没变就不下载。
 * 文本走 blob API（CORS 允许），图片则可以在页面里直接用 raw URL。
 */
export class GiteeContentSource implements ContentSource {
  readonly key: string
  readonly label: string

  readonly #owner: string
  readonly #repo: string
  readonly #ref: string
  readonly #bundlePath: string
  readonly #apiBase: string

  constructor(options: GiteeSourceOptions) {
    this.#owner = options.owner
    this.#repo = options.repo
    this.#ref = options.ref ?? 'master'
    this.#bundlePath = options.bundlePath ?? 'dist/content.json'
    this.#apiBase = (options.apiBase ?? DEFAULT_API).replace(/\/$/, '')

    this.key = `gitee:${this.#owner}/${this.#repo}@${this.#ref}:${this.#bundlePath}${
      options.apiBase === undefined ? '' : `:${this.#apiBase}`
    }`
    this.label = `Gitee · ${this.#owner}/${this.#repo}@${this.#ref}`
  }

  #repoUrl(path: string): string {
    return `${this.#apiBase}/repos/${encodeURIComponent(this.#owner)}/${encodeURIComponent(this.#repo)}${path}`
  }

  async #getJson<T>(url: string, description: string): Promise<T> {
    const response = await fetchWithTimeout(url, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })

    if (response.status === 404) {
      throw new ContentError('not-found', `${description}不存在（404）。确认仓库已公开、分支是 ${this.#ref}。`)
    }

    if (response.status === 403 || response.status === 429) {
      throw new ContentError('rate-limit', `Gitee API 限流或拒绝访问（${response.status}）。`, {
        status: response.status,
        retryAfter: retryAfterOf(response),
      })
    }

    if (!response.ok) {
      throw new ContentError('http', `Gitee API 返回 ${response.status}：${description}`, {
        status: response.status,
      })
    }

    try {
      return (await response.json()) as T
    } catch {
      throw new ContentError('parse', `${description}返回的不是 JSON。`)
    }
  }

  async load(options: SourceLoadOptions): Promise<SourceResult> {
    const tree = await this.#getJson<GiteeTree>(
      `${this.#repoUrl(`/git/trees/${encodeURIComponent(this.#ref)}`)}?recursive=1`,
      '仓库文件树',
    )

    const entry = tree.tree?.find((item) => item.path === this.#bundlePath && item.type === 'blob')
    if (entry === undefined) {
      const suffix = tree.truncated ? '（tree 响应被截断，先确认文件和目录层级）' : ''
      throw new ContentError(
        'not-found',
        `仓库里找不到 ${this.#bundlePath}${suffix}。先在内容仓运行 npm run build 并 push。`,
      )
    }

    if (!options.force && options.cached?.sha === entry.sha) {
      return { status: 'not-modified', sha: entry.sha }
    }

    const blob = await this.#getJson<GiteeBlob>(
      this.#repoUrl(`/git/blobs/${encodeURIComponent(entry.sha)}`),
      `${this.#bundlePath} 的 blob`,
    )

    if (blob.encoding !== 'base64' || typeof blob.content !== 'string') {
      throw new ContentError('invalid', 'Gitee blob 没有返回 base64 内容。')
    }

    let text: string
    try {
      text = decodeBase64Utf8(blob.content)
    } catch {
      throw new ContentError('invalid', '内容不是有效的 UTF-8，检查 Markdown 文件编码。')
    }

    let raw: unknown
    try {
      raw = JSON.parse(text)
    } catch {
      throw new ContentError('parse', `${this.#bundlePath} 不是合法 JSON。`)
    }

    return { status: 'updated', sha: entry.sha, bundle: parseBundle(raw) }
  }
}
